import React, { createContext, useContext, useState, useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import GitLabAPI from '~/api/gitlabApi';

const GlobalPipelineProgressContext = createContext();

export const useGlobalPipelineProgress = () => useContext(GlobalPipelineProgressContext);

export const GlobalPipelineProgressProvider = ({ children }) => {
    const [pipeline, setPipeline] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        let connection = null;
        let isMounted = true;

        const fetchLatestPipeline = async () => {
            try {
                const res = await GitLabAPI.getPipelines(1, 1);
                let latest = null;
                const data = res && res.data;
                const items = data?.Results;
                if (items && items.length > 0) {
                    latest = items[0];
                } else if (Array.isArray(data) && data.length > 0) {
                    latest = data[0];
                }

                if (latest && isMounted) {
                    setPipeline(latest);
                    const status = (latest.status || latest.Status || '').toLowerCase();
                    if (status === 'running' || status === 'pending') {
                        setIsVisible(true);
                    } else {
                        setIsVisible(false);
                    }
                }
            } catch (err) {
                // Ignore API errors
            }
        };

        // 1. Initial fetch
        fetchLatestPipeline();

        // 2. Setup SignalR (delayed to avoid StrictMode double-mount abort)
        const apiUrl = process.env.REACT_APP_API_URL || '';

        const setupSignalR = async () => {
            // Small delay to let StrictMode cleanup run first if it's going to
            await new Promise(resolve => setTimeout(resolve, 100));
            if (!isMounted) return;

            connection = new signalR.HubConnectionBuilder()
                .withUrl(`${apiUrl}/api/gitlabPipelineHub`, {
                    withCredentials: true
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.None)
                .build();

            connection.on("PipelineStatusChanged", (pipelineData) => {
                if (!isMounted) return;
                setPipeline((prev) => {
                    return {
                        ...prev,
                        id: pipelineData.id || (prev ? prev.id : ''),
                        status: pipelineData.status || (prev ? prev.status : ''),
                        ref: pipelineData.ref || (prev ? prev.ref : '')
                    };
                });

                // Handle visibility for real-time updates
                const status = (pipelineData.status || '').toLowerCase();
                if (status === 'running' || status === 'pending') {
                    setIsVisible(true);
                } else if (['success', 'failed', 'canceled'].includes(status)) {
                    // It just finished right now! Keep visible for 5 seconds then hide and reload.
                    setIsVisible(true);
                    setTimeout(() => {
                        setIsVisible(false);
                        window.location.reload();
                    }, 5000);
                } else {
                    setIsVisible(false);
                }
            });

            try {
                await connection.start();
            } catch (err) {
                if (isMounted) {
                    console.error("SignalR GitlabHub connection error:", err);
                }
            }
        };

        setupSignalR();

        return () => {
            isMounted = false;
            if (connection) {
                connection.stop();
            }
        };
    }, []);

    return (
        <GlobalPipelineProgressContext.Provider value={{ pipeline, isRunning: isVisible }}>
            {children}
        </GlobalPipelineProgressContext.Provider>
    );
};
